/**
 * Business calendar for GoalsDash
 *
 * The single source of truth for dates, periods and period keys. Shared by the
 * Worker and the React app, so everything here is pure: no DOM, no
 * dependencies, no reads of the process time zone.
 *
 * CONVENTIONS:
 * - Every date and period is in the business time zone (America/Denver)
 * - Timestamps are Unix seconds
 * - A period starts at 00:00:00 on its first day and ends one second before
 *   the next period starts, so `<=` comparisons work (DST weeks are 167/169h)
 * - Weeks run Sunday to Saturday and are never clipped at month or year ends
 */

export const BUSINESS_TIME_ZONE = 'America/Denver'

export type Timeframe = 'weekly' | 'monthly' | 'quarterly' | 'biannual' | 'yearly'

/** A calendar day in the business time zone, 'YYYY-MM-DD' */
export type CalendarDate = string

export interface CalendarPeriod {
  key: string
  start: number // Unix seconds, 00:00:00 on firstDate
  end: number // Unix seconds, next period's start minus one
  label: string
  firstDate: CalendarDate
  lastDate: CalendarDate
}

const SECONDS_PER_DAY = 86400
// Denver ran on local mean time (UTC-06:59:56) until Nov 1883; 1884 is its first full year of standard time
const FIRST_SUPPORTED_YEAR = 1884
// Midnight on 1884-01-01 in Denver (UTC-7)
const FIRST_SUPPORTED_SECONDS = Date.UTC(FIRST_SUPPORTED_YEAR, 0, 1, 7) / 1000
// Largest instant a Date holds, in Unix seconds
const MAX_DATE_SECONDS = 8.64e12
/** Shown in place of a date that cannot be read */
export const UNREADABLE_DATE = '—'
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const formatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

interface WallClock {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
  second: number
}

function wallClockOf(ts: number): WallClock {
  const values: Record<string, number> = {}
  for (const part of formatter.formatToParts(new Date(ts * 1000))) {
    if (part.type !== 'literal') values[part.type] = Number(part.value)
  }
  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour % 24,
    minute: values.minute,
    second: values.second,
  }
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0')
}

function formatDate(year: number, month: number, day: number): CalendarDate {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`
}

// Calendar arithmetic: whole days since 1970-01-01, using Date.UTC purely as a calculator
function daysOf(date: CalendarDate): number {
  const [year, month, day] = date.split('-').map(Number)
  // setUTCFullYear because Date.UTC reads years 0-99 as 1900-1999
  const d = new Date(Date.UTC(2000, month - 1, day))
  d.setUTCFullYear(year, month - 1, day)
  return d.getTime() / (SECONDS_PER_DAY * 1000)
}

function dateOfDays(days: number): CalendarDate {
  const d = new Date(days * SECONDS_PER_DAY * 1000)
  return formatDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

function dayOfWeekOf(days: number): number {
  return new Date(days * SECONDS_PER_DAY * 1000).getUTCDay()
}

export function parseCalendarDate(value: string): CalendarDate | null {
  if (typeof value !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (year < FIRST_SUPPORTED_YEAR || month < 1 || month > 12 || day < 1) return null
  const d = new Date(Date.UTC(year, month - 1, day))
  d.setUTCFullYear(year)
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null
  }
  return value
}

function requireDate(date: CalendarDate): CalendarDate {
  const parsed = parseCalendarDate(date)
  if (parsed === null) throw new RangeError(`Invalid calendar date: ${String(date)}`)
  return parsed
}

/** The Denver date of `ts`, or '—' when `ts` is not a timestamp a Date can hold. Never throws */
export function calendarDateOf(ts: number): CalendarDate {
  if (typeof ts !== 'number' || !Number.isFinite(ts) || Math.abs(ts) > MAX_DATE_SECONDS) return UNREADABLE_DATE
  const { year, month, day } = wallClockOf(ts)
  return formatDate(year, month, day)
}

export function todayInBusinessZone(now: number = Math.floor(Date.now() / 1000)): CalendarDate {
  return calendarDateOf(now)
}

/** The instant at which the Denver wall clock reads `hour`:00:00 on `date` */
function instantAt(date: CalendarDate, hour: number): number {
  const wall = daysOf(date) * SECONDS_PER_DAY + hour * 3600
  // Denver is UTC-7 (MST) or UTC-6 (MDT); try each and keep the one that reads back right
  for (const offset of [7 * 3600, 6 * 3600]) {
    const ts = wall + offset
    if (calendarDateOf(ts) === date && wallClockOf(ts).hour === hour) return ts
  }
  throw new RangeError(`No such local time: ${date} ${pad(hour)}:00`)
}

export function startOfDay(date: CalendarDate): number {
  return instantAt(requireDate(date), 0)
}

export function endOfDay(date: CalendarDate): number {
  return startOfDay(dateOfDays(daysOf(requireDate(date)) + 1)) - 1
}

export function noonOf(date: CalendarDate): number {
  return instantAt(requireDate(date), 12)
}

/** True when `ts` is a whole second whose Denver date is a supported calendar date (1884-01-01 to 9999-12-31) */
export function isSupportedTimestamp(ts: unknown): ts is number {
  // The numeric floor matters: Intl drops the era, so a BCE instant would otherwise read as a modern date
  return (
    typeof ts === 'number' &&
    Number.isSafeInteger(ts) &&
    ts >= FIRST_SUPPORTED_SECONDS &&
    parseCalendarDate(calendarDateOf(ts)) !== null
  )
}

/** An unreadable `ts` (out of range, or a millisecond value) is not past */
export function isPastCalendarDate(ts: number, now: number = Math.floor(Date.now() / 1000)): boolean {
  const date = calendarDateOf(ts)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false
  return date < todayInBusinessZone(now)
}

function shortMonthName(month: number): string {
  return MONTH_NAMES[month - 1].slice(0, 3)
}

/** 'Mar 15, 2026', the same in every viewer time zone */
export function formatCalendarDate(date: CalendarDate): string {
  const valid = parseCalendarDate(date)
  if (valid === null) return UNREADABLE_DATE
  return `${shortMonthName(Number(valid.slice(5, 7)))} ${Number(valid.slice(8, 10))}, ${valid.slice(0, 4)}`
}

function buildPeriod(
  key: string,
  label: string,
  firstDate: CalendarDate,
  nextFirstDate: CalendarDate
): CalendarPeriod {
  // Not startOfDay: a week holding 1884-01-01 starts on Dec 30, 1883, already on standard time
  return {
    key,
    start: instantAt(firstDate, 0),
    end: instantAt(nextFirstDate, 0) - 1,
    label,
    firstDate,
    lastDate: dateOfDays(daysOf(nextFirstDate) - 1),
  }
}

export function periodForDate(date: CalendarDate, timeframe: Timeframe): CalendarPeriod {
  const valid = requireDate(date)
  const year = Number(valid.slice(0, 4))
  const month = Number(valid.slice(5, 7))

  switch (timeframe) {
    case 'weekly': {
      const days = daysOf(valid)
      const first = dateOfDays(days - dayOfWeekOf(days))
      const next = dateOfDays(days - dayOfWeekOf(days) + 7)
      const last = dateOfDays(days - dayOfWeekOf(days) + 6)
      const label = `${shortMonthName(Number(first.slice(5, 7)))} ${Number(first.slice(8, 10))} – ${shortMonthName(Number(last.slice(5, 7)))} ${Number(last.slice(8, 10))}`
      return buildPeriod(`W${first}`, label, first, next)
    }
    case 'monthly': {
      const first = formatDate(year, month, 1)
      const next = month === 12 ? formatDate(year + 1, 1, 1) : formatDate(year, month + 1, 1)
      return buildPeriod(`${pad(year, 4)}-${pad(month)}`, `${MONTH_NAMES[month - 1]} ${year}`, first, next)
    }
    case 'quarterly': {
      const quarter = Math.floor((month - 1) / 3)
      const first = formatDate(year, quarter * 3 + 1, 1)
      const next = quarter === 3 ? formatDate(year + 1, 1, 1) : formatDate(year, quarter * 3 + 4, 1)
      return buildPeriod(`${pad(year, 4)}-Q${quarter + 1}`, `Q${quarter + 1}-${year}`, first, next)
    }
    case 'biannual': {
      const half = month <= 6 ? 0 : 1
      const first = formatDate(year, half * 6 + 1, 1)
      const next = half === 1 ? formatDate(year + 1, 1, 1) : formatDate(year, 7, 1)
      const label = half === 0 ? `Jan-Jun '${pad(year % 100)}` : `Jul-Dec '${pad(year % 100)}`
      return buildPeriod(`${pad(year, 4)}-H${half + 1}`, label, first, next)
    }
    case 'yearly':
      return buildPeriod(pad(year, 4), String(year), formatDate(year, 1, 1), formatDate(year + 1, 1, 1))
  }
}

export function periodForInstant(ts: number, timeframe: Timeframe): CalendarPeriod {
  return periodForDate(calendarDateOf(ts), timeframe)
}

/** For stored timestamps: the period of `ts`, or null when `ts` cannot be read. Never throws */
export function readablePeriod(ts: number, timeframe: Timeframe): CalendarPeriod | null {
  if (!(ts >= FIRST_SUPPORTED_SECONDS) || parseCalendarDate(calendarDateOf(ts)) === null) return null
  return periodForInstant(ts, timeframe)
}

export function previousPeriod(period: CalendarPeriod, timeframe: Timeframe): CalendarPeriod {
  return periodForInstant(period.start - 1, timeframe)
}

export function nextPeriod(period: CalendarPeriod, timeframe: Timeframe): CalendarPeriod {
  return periodForInstant(period.end + 1, timeframe)
}

/** `count` periods, oldest first; the last one contains `now` */
export function listPeriods(now: number, timeframe: Timeframe, count: number): CalendarPeriod[] {
  const periods: CalendarPeriod[] = []
  let current = periodForInstant(now, timeframe)
  for (let i = 0; i < count; i++) {
    periods.unshift(current)
    current = previousPeriod(current, timeframe)
  }
  return periods
}

export function quarterKeyForDate(date: CalendarDate): string {
  return periodForDate(date, 'quarterly').key
}

/** SlackBot reads these strings without a time zone, so they carry the calendar dates in UTC form */
export function awardPayloadDates<Last extends CalendarDate | null>(period: {
  firstDate: CalendarDate
  lastDate: Last
}): { period_start: string; period_end: Last } {
  return {
    period_start: `${period.firstDate}T00:00:00.000Z`,
    period_end: (period.lastDate === null ? null : `${period.lastDate}T23:59:59.000Z`) as Last,
  }
}
